import { useEffect, useState } from 'react';
import { Modal, Stack, Button, Group, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { coachesApi } from '../../../services/api/coaches';
import { RichTextEditor } from '../../../components/common';
import type { Coach } from '../../../types';

interface EditNotesModalProps {
  opened: boolean;
  onClose: () => void;
  coach: Coach;
  onSaved: (coach: Coach) => void;
}

export function EditNotesModal({ opened, onClose, coach, onSaved }: EditNotesModalProps) {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (opened && coach) {
      setNotes(coach.notes || '');
    }
  }, [opened, coach]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const updatedCoach = await coachesApi.update(coach.id, { notes });
      notifications.show({
        title: 'Success',
        message: 'Internal notes updated successfully',
        color: 'green',
      });
      onSaved(updatedCoach);
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update notes',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Edit Internal Medley Notes" size="lg">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          These notes are only visible to Medley staff and will never be shown to coaches.
        </Text>
        <RichTextEditor
          label="Internal Notes"
          value={notes}
          onChange={setNotes}
          placeholder="Add internal notes about this coach..."
          minHeight={200}
        />
        <Group justify="flex-end" className="modal-footer">
          <Button variant="subtle" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={isSubmitting}>
            Save Notes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
